import { Fragment } from 'react'
import Link from 'next/link'
import styled from 'styled-components'
import { Skeleton } from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import { Label } from '@/components/text'

import { ReactComponent as Separator } from './breadcrumb-separator.svg'

const BreadcrumbNav = styled.nav`
  width: 100%;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    overflow-x: auto;
  }
`

const BreadcrumbList = styled.ol`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  text-align: center;

  a {
    color: ${colors.grey56};
    text-decoration: none;
  }
`

const SeparatorWrapper = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  svg {
    color: ${colors.grey56};
    margin: 0 8px;
    height: 16px;
    width: 12px;
  }
`

const SkeletonState = ({ items }) => {
  return (
    <BreadcrumbNav>
      <BreadcrumbList>
        {items.map((_, index) => {
          const isLast = index >= items.length - 1
          const skeletonWidth = index === 0 ? 40 : index === 1 ? 20 : 70
          return (
            <Fragment key={`breadcrumb-skeleton-${index}`}>
              <BreadcrumbItem>
                <Skeleton.Shape height={16} width={skeletonWidth} />
              </BreadcrumbItem>
              {!isLast && (
                <SeparatorWrapper aria-hidden="true">
                  <Separator />
                </SeparatorWrapper>
              )}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </BreadcrumbNav>
  )
}

const Breadcrumbs = ({ items, loading }) => {
  if (loading) return <SkeletonState items={items} />
  return (
    <BreadcrumbNav aria-label="breadcrumbs">
      <BreadcrumbList itemScope itemType="https://schema.org/BreadcrumbList">
        {items.map(({ label, path, shallow }, index) => {
          const isLast = index >= items.length - 1
          return (
            <Fragment key={path}>
              <BreadcrumbItem
                itemProp="itemListElement"
                itemScope
                itemType="http://schema.org/ListItem"
              >
                <Link
                  href={path}
                  shallow={shallow}
                  prefetch={false}
                  itemProp="item"
                  aria-current={isLast ? 'page' : undefined}
                >
                  <Label itemProp="name" fontSize="14px" as="span">
                    {label}
                  </Label>
                </Link>
                <meta itemProp="position" content={index + 1} />
              </BreadcrumbItem>
              {!isLast && (
                <SeparatorWrapper aria-hidden="true">
                  <Separator />
                </SeparatorWrapper>
              )}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </BreadcrumbNav>
  )
}

export default Breadcrumbs
